import { useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useGetSchoolsQuery } from "./schoolsMutation";
import { normalizeSchoolDetails } from "./schoolTransform";

// Invitation code exact format: 4 alphanumeric - 6 alphanumeric - 6 alphanumeric
export const INVITATION_CODE_REGEX =
  /^[A-Za-z0-9]{4}-[A-Za-z0-9]{6}-[A-Za-z0-9]{6}$/;

const useSchoolQueries = (initialPage = 1, initialLimit = 100) => {
  const location = useLocation();
  const isActive = location.pathname === "/schools";
  const hasInitializedRef = useRef(false);
  const wasInitializingRef = useRef(false);

  // Mark as initialized immediately when page becomes active for the first time
  if (isActive && !hasInitializedRef.current) {
    hasInitializedRef.current = true;
    wasInitializingRef.current = true;
  } else if (isActive) {
    wasInitializingRef.current = false;
  }

  const [page, setPage] = useState(initialPage);
  const [limit, setLimit] = useState(initialLimit);
  const [searchBy, setSearchBy] = useState("school_name");
  const [searchQuery, setSearchQuery] = useState("");

  // Phone search: exactly 10 digits. Invitation code: exact pattern. Others: at least 3 characters
  const isPhoneSearch = searchBy === "school_phone";
  const isInvitationCodeSearch = searchBy === "invitation_code";
  const phoneDigitsOnly = searchQuery.trim().replace(/\D/g, "");
  const isValidPhone = phoneDigitsOnly.length > 8;
  const invitationCodeTrimmed = searchQuery.trim();
  const isValidInvitationCode = INVITATION_CODE_REGEX.test(
    invitationCodeTrimmed,
  );
  const hasSearchQuery = isPhoneSearch
    ? isValidPhone
    : isInvitationCodeSearch
      ? isValidInvitationCode
      : searchQuery.trim().length >= 3;

  const {
    data: schoolsData,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useGetSchoolsQuery(
    {
      page,
      limit,
      searchBy,
      searchQuery: isPhoneSearch
        ? phoneDigitsOnly
        : isInvitationCodeSearch
          ? invitationCodeTrimmed.toUpperCase()
          : searchQuery,
    },
    {
      // Only refetch on mount if this is the first initialization
      refetchOnMountOrArgChange: wasInitializingRef.current,
      skip: !hasInitializedRef.current || !hasSearchQuery,
    },
  );

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleLimitChange = (newLimit) => {
    setLimit(Math.min(Math.max(newLimit, 1), 2000));
    setPage(1);
  };

  const handleSearchByChange = (by) => {
    setSearchBy(by);
    setPage(1);
  };

  const handleSearchQueryChange = (query) => {
    setSearchQuery(query || "");
    setPage(1);
  };

  const schools = schoolsData?.results || [];
  const totalCount = schoolsData?.total_count || 0;

  return {
    schools,
    totalCount,
    currentPage: page,
    itemsPerPage: limit,
    searchBy,
    searchQuery,
    isPhoneSearch,
    isValidPhone,
    phoneDigitsOnly,
    isInvitationCodeSearch,
    isValidInvitationCode,
    isLoading: isLoading || isFetching,
    error: error
      ? {
          status: error.status,
          message: error.message || "Failed to fetch schools",
        }
      : null,
    handlePageChange,
    handleLimitChange,
    handleSearchByChange,
    handleSearchQueryChange,
    refetch,
  };
};

export default useSchoolQueries;
export { normalizeSchoolDetails };
